import { Button, RiAddLine, RiSaveLine, Stack } from 'component-library';
import { FormProvider, SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { RoadmapNode } from '@/api/rest/resources/types/organization';
import { Workstream } from '@/api/rest/resources/types/workstreams';
import { usePathSet } from '@/hooks/usePathSet';
import { useSelectedIndicatorProducts } from '@/pages/shared/indicators/hooks/useIndicatorProducts';
import { useAddWorkstream } from '@/pages/shared/workstreams/pages/hooks/useAddWorkstream';
import { useEditWorkstream } from '@/pages/shared/workstreams/pages/hooks/useEditWorkstream';
import { isNullish } from '@/utils/booleans';

import { useWorkstreamCreateForm, WorkstreamCreateFormFieldValues } from '../hooks/useFormControls';
import { WorkstreamFormInfo } from './WorkstreamFormInfo';
import { WorkstreamFormPlotGroups } from './WorkstreamFormPlotGroups';
import { WorkstreamsFormIndicatorProducts } from './WorkstreamsFormIndicatorProducts';
import { WorkstreamsMilestonesForm } from './WorkstreamsMilestonesForm';

type EditWorkstreamFormProps = {
  workstream?: Workstream;
};

export const WorkstreamForm = ({ workstream }: EditWorkstreamFormProps) => {
  const orgIndicatorProducts = useSelectedIndicatorProducts();

  const isEditMode = !isNullish(workstream) && !isNullish(workstream?.id);

  const defaultValues = isEditMode
    ? {
        title: workstream.title,
        description: workstream.description ?? '',
        plotGroups: workstream.plot_groups,
        indicatorProducts: workstream.indicator_products ?? [],
        milestones: workstream.roadmap_nodes ?? [],
        progress: workstream.progress ?? undefined,
      }
    : {
        title: '',
        description: '',
        plotGroups: [],
        indicatorProducts: orgIndicatorProducts.map((p) => p.id),
        milestones: [],
        progress: undefined,
      };

  const { t } = useTranslation();
  const form = useWorkstreamCreateForm({
    defaultValues,
  });
  const { submit: editSubmit, isSubmitting: isEditSubmitting } = useEditWorkstream(workstream?.id ?? '');
  const { submit: addSubmit, isSubmitting: isAddSubmitting } = useAddWorkstream();

  const isSubmitting = isEditMode ? isEditSubmitting : isAddSubmitting;

  const navigate = useNavigate();
  const paths = usePathSet();

  const onSubmitEdit: SubmitHandler<WorkstreamCreateFormFieldValues> = async (values) => {
    const { title, description, plotGroups, milestones, indicatorProducts, progress } = values;

    const validatedMilestones =
      milestones.length > 0
        ? ([
            {
              ...milestones[0],
              title: (milestones[0] as Partial<RoadmapNode>)?.title || `${title} - root milestone`,
            },
            ...milestones.slice(1),
          ] as Partial<RoadmapNode>[])
        : milestones;

    await editSubmit(
      {
        title,
        description: description || null,
        plot_groups: plotGroups,
        indicator_products: indicatorProducts,
        roadmap_nodes: validatedMilestones,
        progress: progress ?? undefined,
      },
      () => navigate(paths.workstreams),
    );
  };

  const onCreateSubmit: SubmitHandler<WorkstreamCreateFormFieldValues> = async (values) => {
    const { title, description, plotGroups, milestones, indicatorProducts, progress } = values;

    const hasMilestones = milestones.length > 0 && (milestones?.[0]?.children?.length ?? 0) > 0;

    const validatedMilestones = hasMilestones
      ? ([
          {
            ...milestones?.[0],
            title: `${title} - root milestone`, // Workstreams have a root milestone, but this is hidden to users, so we provide here a title, since this is a required field
          },
        ] as Partial<RoadmapNode>[])
      : [];

    await addSubmit(
      {
        title,
        description: description || null,
        plot_groups: plotGroups,
        indicator_products: indicatorProducts,
        roadmap_nodes: validatedMilestones,
        progress: progress ?? undefined,
      },
      () => navigate(paths.workstreams),
    );
  };

  const onSubmit = isEditMode ? onSubmitEdit : onCreateSubmit;

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Stack spacing={6}>
          <WorkstreamFormInfo form={form} />
          <WorkstreamsMilestonesForm form={form} />
          <WorkstreamFormPlotGroups form={form} />
          <WorkstreamsFormIndicatorProducts form={form} />
          <Stack direction='row' className='justify-end'>
            <Button
              type='submit'
              variant='contained'
              className='w-fit'
              loading={isSubmitting}
              disabled={isSubmitting || !form.formState.isValid}
              leftAdornment={isEditMode ? <RiSaveLine /> : <RiAddLine />}
            >
              {t(`global.ui.buttons.${isEditMode ? 'save' : 'create'}`)}
            </Button>
          </Stack>
        </Stack>
      </form>
    </FormProvider>
  );
};
